import type { WorkflowRunStatus } from '../hooks/useWorkflowRuns';

/**
 * Shared by RunDetailsPanel and LiveRunPanel so the two headers' status pills agree.
 * Deliberately keyed on the full `WorkflowRunStatus` (includes `timeout`) even
 * though both call sites currently index it with the narrower
 * `WorkflowRunWithStepsDto['status']` — it mirrors `runStatusLabel` below,
 * which already handles `timeout`, and the runs list does carry `timeout`
 * statuses. Don't narrow this back to the DTO union.
 */
export const statusVariant: Record<
  WorkflowRunStatus,
  'neutral' | 'positive' | 'negative' | 'notice'
> = {
  queued: 'neutral',
  running: 'notice',
  success: 'positive',
  failed: 'negative',
  inconclusive: 'neutral',
  canceled: 'neutral',
  timeout: 'negative',
};

export const formatDuration = (durationMs: number | null) => {
  if (durationMs === null || durationMs === undefined || durationMs < 0) return '—';
  if (durationMs === 0) return '—';
  if (durationMs < 1000) return '<1s';
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
};

export const formatRelativeTime = (value: string | null) => {
  if (!value) return '—';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '—';
  const deltaMs = Date.now() - timestamp;
  const deltaSeconds = Math.floor(Math.abs(deltaMs) / 1000);
  const future = deltaMs < 0;

  if (deltaSeconds < 10) return future ? 'in a few seconds' : 'just now';
  if (deltaSeconds < 60) return future ? `in ${deltaSeconds}s` : `${deltaSeconds}s ago`;

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) return future ? `in ${deltaMinutes}m` : `${deltaMinutes}m ago`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return future ? `in ${deltaHours}h` : `${deltaHours}h ago`;

  const deltaDays = Math.floor(deltaHours / 24);
  return future ? `in ${deltaDays}d` : `${deltaDays}d ago`;
};

export const runStatusLabel = (status: WorkflowRunStatus) => {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'success':
      return 'Success';
    case 'failed':
      return 'Failed';
    case 'inconclusive':
      return 'Platform error';
    case 'canceled':
      return 'Canceled';
    case 'timeout':
      return 'Timed Out';
    default:
      return status;
  }
};
